import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { isNativeApp, isIOS, webOrigin } from "@/lib/nativeBridge";
import IosPurchaseNotice from "@/components/IosPurchaseNotice";
import api from "@/lib/api";
import { usePublicPrices } from "@/lib/usePublicPrices";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useUserPlanTier } from "@/hooks/useUserPlanTier";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DogIcon as Dog, TrashIcon as Trash, HeartStraightIcon as HeartStraight, ClockIcon as Clock, FlameIcon as Flame, ArrowClockwiseIcon as ArrowClockwise } from "@phosphor-icons/react";
import { formatDate, thumbUrl, formatApiError } from "@/lib/utils-app";
import PromoCodeInput from "@/components/PromoCodeInput";
import CompBanner from "@/components/CompBanner";
import { toast } from "sonner";

export default function MyBreedingListings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewRow, setRenewRow] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/breeding/my")
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    try {
      await api.delete(`/breeding/${id}`);
      toast.success("Listing removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const now = new Date().toISOString();
  const active = rows.filter((r) => r.expires_at > now);
  const expired = rows.filter((r) => r.expires_at <= now);

  return (
    <AppShell>
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">Library</p>
          <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">My breeding listings</h1>
          <p className="text-[#6B6B6B] mt-2 max-w-2xl">
            Your active directory entries and their history. Expired listings can be renewed from the
            dog's page.
          </p>
        </div>
        <Link to="/breeding">
          <Button variant="outline" className="rounded-full border-[#E5E5E5]">Browse directory</Button>
        </Link>
      </div>

      {loading ? (
        <div className="text-[#6B6B6B]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-16 text-center" data-testid="my-listings-empty">
          <HeartStraight size={48} weight="duotone" className="text-crimson mx-auto mb-4" />
          <h2 className="font-spectral text-2xl tracking-tight">No listings yet</h2>
          <p className="text-[#6B6B6B] mt-2 max-w-md mx-auto">
            From any of your dog pages, open the Breeding tab and list them to appear in the directory.
          </p>
          <Link to="/dogs" className="inline-block mt-6">
            <Button className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white">
              Browse my dogs
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-10">
          {active.length > 0 && (
            <Section title="Active" count={active.length} rows={active} onRemove={remove} onRenew={setRenewRow} />
          )}
          {expired.length > 0 && (
            <Section title="Expired" count={expired.length} rows={expired} onRemove={remove} onRenew={setRenewRow} dim />
          )}
        </div>
      )}

      <RenewDialog row={renewRow} onClose={() => setRenewRow(null)} />
    </AppShell>
  );
}

function Section({ title, count, rows, onRemove, onRenew, dim }) {
  return (
    <div>
      <h2 className="font-spectral text-2xl tracking-tight mb-4">
        {title} <span className="text-[#6B6B6B] text-base font-manrope">({count})</span>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rows.map((r) => (
          <Card key={r.id} row={r} onRemove={onRemove} onRenew={onRenew} dim={dim} />
        ))}
      </div>
    </div>
  );
}

function Card({ row, onRemove, onRenew, dim }) {
  const d = row.dog;
  const now = Date.now();
  const expiresTs = new Date(row.expires_at).getTime();
  const daysLeft = Math.round((expiresTs - now) / (1000 * 60 * 60 * 24));
  const expiringSoon = daysLeft >= 0 && daysLeft <= 7;
  const expired = expiresTs <= now;
  return (
    <div
      className={`bg-white border border-[#E5E5E5] rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)] ${
        dim ? "opacity-60" : ""
      }`}
      data-testid={`my-listing-${row.id}`}
    >
      <div className="aspect-[4/3] bg-[#FAFAFA] relative">
        {d.photos?.[0] ? (
          <img src={thumbUrl(d.photos[0])} alt={d.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Dog size={40} weight="duotone" className="text-[#6B6B6B]/40" />
          </div>
        )}
        {(expiringSoon || expired) && (
          <span
            className={`absolute top-3 right-3 text-[10px] font-bold uppercase tracking-[0.15em] rounded-full px-2.5 py-1 ${
              expired ? "bg-[#6B6B6B] text-white" : "bg-[#C1272D] text-white"
            }`}
          >
            {expired ? "Expired" : `${daysLeft}d left`}
          </span>
        )}
      </div>
      <div className="p-5">
        <Link to={`/dogs/${d.id}`} className="font-spectral text-xl tracking-tight hover:text-crimson">
          {d.name}
        </Link>
        <p className="text-xs text-[#6B6B6B] mt-0.5">{d.breed}</p>
        <div className="mt-3 text-xs text-[#6B6B6B] space-y-1">
          <div className="flex items-center gap-1.5">
            <Clock size={12} /> {row.days}-day term · {expired ? "expired" : "expires"}{" "}
            {formatDate(row.expires_at)}
          </div>
          {row.next_heat && (
            <div className="flex items-center gap-1.5 text-crimson">
              <Flame size={12} weight="duotone" /> Next heat {formatDate(row.next_heat.start_date)}
            </div>
          )}
          {row.fee_requested > 0 && (
            <div>Fee requested: ${row.fee_requested.toFixed(2)}</div>
          )}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {(expiringSoon || expired) && (
            <Button
              onClick={() => onRenew(row)}
              size="sm"
              className="w-full rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white col-span-2"
              data-testid={`renew-listing-${row.id}`}
            >
              <ArrowClockwise size={14} className="mr-1" /> Renew listing
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`rounded-full border-[#C1272D]/30 text-[#C1272D] hover:bg-[#C1272D]/5 w-full ${
                  expiringSoon || expired ? "col-span-2" : "col-span-2"
                }`}
                data-testid={`remove-listing-${row.id}`}
              >
                <Trash size={14} className="mr-1" /> Remove
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove listing for {d.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  The directory entry will be deleted immediately. Your payment is non-refundable.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => onRemove(row.id)} data-testid={`confirm-remove-${row.id}`}>
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}

function RenewDialog({ row, onClose }) {
  const priorPackage = (() => {
    if (!row) return "breeding_listing_30d";
    if (row.days >= 300) return "breeding_listing_1y";
    if (row.days >= 60) return "breeding_listing_90d";
    return "breeding_listing_30d";
  })();
  const [tier, setTier] = useState(priorPackage);
  const [description, setDescription] = useState("");
  const [fee, setFee] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [promo, setPromo] = useState(null);
  const { isProOrElite, isElite } = useUserPlanTier();

  useEffect(() => {
    if (!row) return;
    setTier(priorPackage);
    setDescription(row.description || "");
    setFee(row.fee_requested ? String(row.fee_requested) : "");
    setPromo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row]);

  const { prices } = usePublicPrices();
  const tiers = [
    { id: "breeding_listing_30d", label: "30 days", price: prices("breeding_listing_30d", "$14.99"), amount: prices.amount("breeding_listing_30d", 14.99) },
    { id: "breeding_listing_90d", label: "90 days", price: prices("breeding_listing_90d", "$29.99"), amount: prices.amount("breeding_listing_90d", 29.99) },
    { id: "breeding_listing_1y",  label: "1 year",  price: prices("breeding_listing_1y", "$49.99"),  amount: prices.amount("breeding_listing_1y", 49.99) },
  ];
  const tierAmount = (tiers.find((t) => t.id === tier) || {}).amount || 14.99;

  // Reset promo when tier changes
  useEffect(() => {
    setPromo(null);
  }, [tier]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: tier,
        origin_url: webOrigin(),
        dog_id: row.dog.id,
        listing_description: description,
        listing_fee_requested: parseFloat(fee) || 0,
        ...(promo?.valid ? { promo_code: promo.code } : {}),
      });
      if (data.free) {
        toast.success("Listing renewed");
        onClose();
        return;
      }
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  if (!row) return null;

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Renew listing for {row.dog.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <p className="text-sm text-[#6B6B6B]">
            We've pre-filled the previous tier and description. Adjust anything you want, then continue
            to checkout.
          </p>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2 block">
              Listing term
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {tiers.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTier(t.id)}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    tier === t.id
                      ? "border-[#C1272D] bg-[#C1272D]/5 text-[#0E0E0E]"
                      : "border-[#E5E5E5] bg-white text-[#6B6B6B] hover:border-[#0E0E0E]/20"
                  }`}
                  data-testid={`renew-tier-${t.id}`}
                >
                  <div className="font-semibold text-ink">{t.label}</div>
                  <div className="text-xs mt-1">
                    {isProOrElite ? <span className="text-emerald-700 font-semibold">Free with plan</span> : t.price}
                  </div>
                  {isElite && t.id === "breeding_listing_30d" && (
                    <div className="text-[10px] text-gold-dark mt-0.5">+ featured · auto-renew</div>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Description
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="mt-1"
              data-testid="renew-description"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Stud fee requested (optional, USD)
            </Label>
            <Input
              type="number"
              value={fee}
              min="0"
              step="0.01"
              onChange={(e) => setFee(e.target.value)}
              className="mt-1 rounded-lg"
              data-testid="renew-fee"
            />
          </div>
          {!isProOrElite && (
          <div>
            {!isNativeApp() && (
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-1 block">
              Promo code (optional)
            </Label>
            )}
            <CompBanner className="mb-3" />
            <PromoCodeInput
              kind="breeding_listing"
              amount={tierAmount}
              applied={promo}
              onApplied={(p) => {
                setPromo(p);
                toast.success(`${p.code} applied — final $${p.final.toFixed(2)}`);
              }}
              onCleared={() => setPromo(null)}
            />
          </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          {isIOS() && !isProOrElite ? (
            <IosPurchaseNotice label="Listing renewal" className="w-full" />
          ) : (
          <Button
            onClick={submit}
            disabled={submitting}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
            data-testid="renew-submit-btn"
          >
            {submitting
              ? (isProOrElite ? "Renewing…" : "Redirecting…")
              : isProOrElite
              ? "Renew for free"
              : promo?.valid
              ? `Pay $${promo.final.toFixed(2)} & renew`
              : "Renew & pay"}
          </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
